import { supabase } from "@/integrations/supabase/client";

/* ============================================================================
   Time-tracking + personal journal API for the Operator Work Studio.
   ============================================================================ */

export interface TimeEntry {
  id: string;
  user_id: string;
  project_id: string;
  task_id: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface OperatorJournalEntry {
  id: string;
  user_id: string;
  entry_date: string; // yyyy-MM-dd
  content: string;
  updated_at: string;
}

/* ---------- Active timer ---------- */

export async function getActiveTimer(userId: string): Promise<TimeEntry | null> {
  if (!userId) return null;
  const { data } = await supabase
    .from("time_entries" as any)
    .select("*")
    .eq("user_id", userId)
    .is("ended_at", null)
    .order("started_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data as any) ?? null;
}

export async function startTimer(p: {
  user_id: string;
  project_id: string;
  task_id?: string | null;
  note?: string | null;
}): Promise<TimeEntry> {
  // Stop any existing active timer first.
  const active = await getActiveTimer(p.user_id);
  if (active) {
    await supabase
      .from("time_entries" as any)
      .update({ ended_at: new Date().toISOString() } as any)
      .eq("id", active.id);
  }
  const { data, error } = await supabase
    .from("time_entries" as any)
    .insert({
      user_id: p.user_id,
      project_id: p.project_id,
      task_id: p.task_id ?? null,
      note: p.note ?? null,
      started_at: new Date().toISOString(),
    } as any)
    .select("*")
    .single();
  if (error) throw error;
  emitTimerChanged();
  return data as any;
}

export async function stopTimer(entryId: string, note?: string | null): Promise<void> {
  const patch: any = { ended_at: new Date().toISOString() };
  if (note !== undefined) patch.note = note;
  const { error } = await supabase
    .from("time_entries" as any)
    .update(patch)
    .eq("id", entryId);
  if (error) throw error;
  emitTimerChanged();
}

export async function updateEntry(id: string, patch: Partial<TimeEntry>): Promise<void> {
  const safe: any = { ...patch };
  delete safe.id; delete safe.user_id; delete safe.created_at;
  delete safe.duration_seconds; delete safe.updated_at;
  const { error } = await supabase.from("time_entries" as any).update(safe).eq("id", id);
  if (error) throw error;
  emitTimerChanged();
}

export async function deleteEntry(id: string): Promise<void> {
  const { error } = await supabase.from("time_entries" as any).delete().eq("id", id);
  if (error) throw error;
  emitTimerChanged();
}

/** Manual entry (no live timer). */
export async function createManualEntry(p: {
  user_id: string;
  project_id: string;
  task_id?: string | null;
  started_at: string;
  ended_at: string;
  note?: string | null;
}): Promise<TimeEntry> {
  const { data, error } = await supabase
    .from("time_entries" as any)
    .insert({
      user_id: p.user_id,
      project_id: p.project_id,
      task_id: p.task_id ?? null,
      started_at: p.started_at,
      ended_at: p.ended_at,
      note: p.note ?? null,
    } as any)
    .select("*")
    .single();
  if (error) throw error;
  return data as any;
}

/* ---------- Listings ---------- */

/* ---------- Cross-tab / cross-page sync ---------- */
export const TIMER_CHANGED_EVENT = "work-studio:timer-changed";
export function emitTimerChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TIMER_CHANGED_EVENT));
}

export async function listEntriesInRange(
  userId: string,
  fromISO: string,
  toISO: string,
): Promise<TimeEntry[]> {
  if (!userId) return [];
  const { data } = await supabase
    .from("time_entries" as any)
    .select("*")
    .eq("user_id", userId)
    .gte("started_at", fromISO)
    .lt("started_at", toISO)
    .order("started_at", { ascending: false });
  return (data ?? []) as any;
}

/* ---------- Operator's cross-project tasks ---------- */

export interface OperatorTaskLite {
  id: string;
  project_id: string;
  project_name: string;
  client_name: string | null;
  title: string;
  status: string;
  deadline_at: string | null;
  completed_at: string | null;
  milestone_id: string | null;
}

/** Tasks across all projects where the user is assigned. */
export async function listAssignedTasksForUser(userId: string): Promise<OperatorTaskLite[]> {
  if (!userId) return [];
  const { data: rows } = await supabase
    .from("task_assignees" as any)
    .select("task_id")
    .eq("user_id", userId);
  const ids = Array.from(new Set((rows ?? []).map((r: any) => r.task_id))).filter(Boolean);
  if (ids.length === 0) return [];
  const { data: tasks } = await supabase
    .from("tasks" as any)
    .select("id, project_id, title, status, deadline_at, completed_at, milestone_id")
    .in("id", ids);
  const projectIds = Array.from(new Set((tasks ?? []).map((t: any) => t.project_id)));
  const { data: projects } = await supabase
    .from("projects")
    .select("id, name, clients ( company_name )")
    .in("id", projectIds);
  const pmap = new Map((projects ?? []).map((p: any) => [p.id, p]));
  return (tasks ?? []).map((t: any) => {
    const p = pmap.get(t.project_id);
    return {
      id: t.id,
      project_id: t.project_id,
      project_name: p?.name ?? "—",
      client_name: (p?.clients as any)?.company_name ?? null,
      title: t.title,
      status: t.status,
      deadline_at: t.deadline_at,
      completed_at: t.completed_at,
      milestone_id: t.milestone_id,
    } as OperatorTaskLite;
  });
}

/* ---------- Cross-project meetings & milestones for an operator ---------- */

export interface OperatorMeetingLite {
  id: string;
  project_id: string;
  project_name: string;
  title: string;
  starts_at: string;
  duration_min: number;
  join_url: string | null;
  status: string;
}

export async function listUpcomingMeetingsForUser(
  userId: string,
  horizonDays = 14,
): Promise<OperatorMeetingLite[]> {
  if (!userId) return [];
  const { data: members } = await supabase
    .from("project_members")
    .select("project_id")
    .eq("user_id", userId);
  const projectIds = Array.from(new Set((members ?? []).map((m: any) => m.project_id)));
  if (projectIds.length === 0) return [];
  const now = new Date();
  const horizon = new Date(now.getTime() + horizonDays * 24 * 60 * 60 * 1000);
  const { data: meetings } = await supabase
    .from("meetings")
    .select("id, project_id, title, starts_at, duration_min, join_url, status")
    .in("project_id", projectIds)
    .gte("starts_at", now.toISOString())
    .lte("starts_at", horizon.toISOString())
    .order("starts_at", { ascending: true });
  const { data: projects } = await supabase
    .from("projects").select("id, name").in("id", projectIds);
  const pmap = new Map((projects ?? []).map((p: any) => [p.id, p.name]));
  return (meetings ?? []).map((m: any) => ({
    id: m.id,
    project_id: m.project_id,
    project_name: pmap.get(m.project_id) ?? "—",
    title: m.title,
    starts_at: m.starts_at,
    duration_min: m.duration_min,
    join_url: m.join_url,
    status: m.status,
  }));
}

export interface OperatorMilestoneLite {
  id: string;
  project_id: string;
  project_name: string;
  title: string;
  due_date: string | null;
  status: string;
}

export async function listUpcomingMilestonesForUser(userId: string): Promise<OperatorMilestoneLite[]> {
  if (!userId) return [];
  const { data: members } = await supabase
    .from("project_members")
    .select("project_id")
    .eq("user_id", userId);
  const projectIds = Array.from(new Set((members ?? []).map((m: any) => m.project_id)));
  if (projectIds.length === 0) return [];
  const { data: ms } = await supabase
    .from("milestones")
    .select("id, project_id, title, due_date, status")
    .in("project_id", projectIds)
    .neq("status", "completed")
    .order("due_date", { ascending: true, nullsFirst: false });
  const { data: projects } = await supabase
    .from("projects").select("id, name").in("id", projectIds);
  const pmap = new Map((projects ?? []).map((p: any) => [p.id, p.name]));
  return (ms ?? []).map((m: any) => ({
    id: m.id,
    project_id: m.project_id,
    project_name: pmap.get(m.project_id) ?? "—",
    title: m.title,
    due_date: m.due_date,
    status: m.status,
  }));
}

/* ---------- Journal ---------- */

export async function getJournal(userId: string, dateISO: string): Promise<OperatorJournalEntry | null> {
  if (!userId) return null;
  const { data } = await supabase
    .from("operator_journal" as any)
    .select("*")
    .eq("user_id", userId)
    .eq("entry_date", dateISO)
    .maybeSingle();
  return (data as any) ?? null;
}

export async function saveJournal(userId: string, dateISO: string, content: string): Promise<void> {
  const existing = await getJournal(userId, dateISO);
  if (existing) {
    const { error } = await supabase
      .from("operator_journal" as any)
      .update({ content } as any)
      .eq("id", existing.id);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from("operator_journal" as any)
    .insert({ user_id: userId, entry_date: dateISO, content } as any);
  if (error) throw error;
}

/* ---------- Helpers ---------- */

export function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function startOfWeek(d = new Date()): Date {
  const date = new Date(d);
  const day = date.getDay(); // 0 Sun … 1 Mon
  const diff = (day + 6) % 7; // make Monday start
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - diff);
  return date;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function fmtDuration(sec: number): string {
  if (!sec || sec < 0) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function fmtDurationClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(ss)}`;
}